/**
 * Who sees an item: the organization (everyone in the space), or, private, only the members and agents on its list
 * (and admins who chose to see other members' private items). The list stays while it's the organization's, for
 * making it private again; making it private always puts you on it. Independent of the triage area.
 */
import { useRef, useState } from "preact/hooks";
import { ApiError, api } from "../../api/client.ts";
import type { Guest, ItemDetail, Member, SpaceOut, Visibility } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Option } from "../../lib/data.ts";
import { shell } from "../../lib/native.ts";
import { spaceApi } from "../../lib/paths.ts";
import { nameOf } from "../../lib/people.ts";
import { useAction } from "../../lib/resource.ts";
import { atLeast } from "../../lib/roles.ts";
import { me } from "../../lib/state.ts";
import { fold } from "../../lib/text.ts";
import { titleOf } from "../../lib/titles.ts";
import { toast } from "../../lib/toast.ts";
import { useId } from "../../lib/uid.ts";
import { isPrivate, itemLink } from "../../lib/visibility.ts";
import { Avatar } from "../../ui/Avatar.tsx";
import { IconButton } from "../../ui/Button.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { ConfirmDialog, Dialog } from "../../ui/Dialog.tsx";
import { Input, Select, Switch } from "../../ui/form.tsx";
import { CloseIcon, LinkIcon, OrgIcon, PrivateIcon, PublicIcon } from "../../ui/icons.tsx";
import { Badge, Banner, CopyButton } from "../../ui/misc.tsx";
import { placePopover } from "../../ui/popover.ts";
import { ICON_ONLY_NARROW } from "../../ui/styles.ts";

/** What a list row, a triage area row or an item says about who sees it. */
interface Visible {
  id: string;
  ver?: string;
  visibility: Visibility;
  private_to?: readonly string[] | null;
  /** Its public link and what its page shows. */
  public?: { token: string; review?: boolean; conversation?: boolean; comments?: boolean } | null;
  /** People from outside the organization it's shared with. */
  guests?: readonly string[] | null;
  /** …who they are (an item's page knows). */
  invited?: readonly Guest[];
}

/** Who's on its list, in words: you alone say nothing more; one other, their name; more, how many. */
function peopleText(people: readonly string[], members: readonly Member[] | undefined): string {
  const others = people.filter((p) => p !== me.value?.id);
  if (others.length === 0) {
    return t("visibility.private");
  }
  if (people.length === 1) {
    return t("visibility.privateOf", { name: nameOf(members, others[0] ?? "") });
  }
  return t("visibility.privatePeople", { count: people.length });
}

/**
 * Who sees it, as a mark beside its title: a padlock when it's private (who's on its list on hover), a globe when
 * anyone with its link does; nothing when it's the organization's.
 */
export function VisibilityMark({
  item,
  members,
  size = 14,
}: {
  item: Visible;
  members: readonly Member[] | undefined;
  size?: number;
}) {
  if (item.visibility === "org") {
    return null;
  }
  const people = item.private_to ?? [];
  const words = isPrivate(item) ? peopleText(people, members) : t("visibility.publicMark");
  // On hover, everyone on its list.
  const all =
    isPrivate(item) && people.length > 1 ? `${words}: ${people.map((p) => nameOf(members, p)).join(", ")}` : words;
  return (
    <span title={all} class="inline-flex shrink-0 items-center align-middle text-zinc-500">
      {isPrivate(item) ? <PrivateIcon size={size} /> : <PublicIcon size={size} />}
      <span class="sr-only">{words}</span>
    </span>
  );
}

/** How many names a list says before "and N more". */
const NAMED = 3;

/** `names`, the first few of them, then how many more. */
function namesOf(names: readonly string[]): string {
  const shown = names.slice(0, NAMED).join(", ");
  return names.length > NAMED ? t("visibility.andMore", { names: shown, count: names.length - NAMED }) : shown;
}

/**
 * Loud, on an item seen from outside the organization: anyone with its link (public), and the guests it's shared
 * with (how many, who). Nothing when it stays inside.
 */
export function OutsideBanner({ item, panel }: { item: Visible; panel: string }) {
  const pub = item.visibility === "public";
  const guests = item.invited ?? [];
  if (!pub && guests.length === 0) {
    return null;
  }
  return (
    // Its Share panel, opened right from here.
    <Banner icon={<PublicIcon size={18} />} action={{ label: t("visibility.configure"), popovertarget: panel }}>
      {pub ? (
        <p>
          <b class="font-semibold">{`${t("visibility.bannerTitlePublic")}:`}</b> {t("visibility.bannerPublic")}
        </p>
      ) : null}
      {/* Public says it all (anyone, guests too). */}
      {!pub && guests.length > 0 ? (
        <p>
          <b class="font-semibold">{`${t("visibility.bannerTitleGuests")}:`}</b>{" "}
          {t("visibility.bannerGuests", { count: guests.length, names: namesOf(guests.map((g) => g.name)) })}
        </p>
      ) : null}
    </Banner>
  );
}

const PANEL =
  "fixed m-0 w-96 max-w-[calc(100vw-1rem)] flex-col gap-4 rounded-lg border border-zinc-200 bg-white p-4 text-sm shadow-lg [&:popover-open]:flex";
const SECTION = "text-xs font-medium text-zinc-500";
/** What each change of who has access says. */
const MADE = {
  private: "visibility.madePrivate",
  org: "visibility.madeOrg",
  public: "visibility.madePublic",
} as const;
/** How many of the organization's other members show as faces. */
const FACES = 4;
/** A change to who has access (as the item's PATCH takes it; a folder's too, but for invitations). */
export interface Change {
  visibility?: Visibility;
  private_to?: string[];
  invite?: string[];
  guests?: string[];
  public?: { review?: boolean; conversation?: boolean; comments?: boolean };
  /** Share its private attachments outside too (asked first). */
  share_private?: boolean;
}

/** An attachment, named. */
interface Named {
  id: string;
  title: string;
}

/** The server's refusal to share private attachments outside unasked. */
const PRIVATE_ATTACHMENTS = "private_attachments";
/** How many people the search offers at once. */
const OFFERS = 6;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/u;
/** The search's option for sharing with an address from outside: this, then the address. */
const INVITE = "invite:";

/** Who `q` finds among `members` (by name or address), and, for an address none of them has, a way to invite it. */
function peopleFor(q: string, members: readonly Member[], known: readonly string[], invites = true): Option[] {
  const needle = q.trim().toLowerCase();
  // Names accents and case aside ("conceicao" finds Conceição), as mentions do.
  const name = fold(needle);
  const found = members
    .filter((m) => !needle || fold(m.name).includes(name) || m.email?.toLowerCase().includes(needle))
    .slice(0, OFFERS)
    .map((m) => ({ value: m.id, label: m.name, ...(m.email ? { hint: m.email } : {}) }));
  const outside =
    invites && EMAIL.test(needle) && !known.includes(needle) && !members.some((m) => m.email?.toLowerCase() === needle);
  return outside
    ? [
        ...found,
        {
          value: `${INVITE}${needle}`,
          label: t("visibility.invite", { email: needle }),
          hint: t("visibility.outside"),
        },
      ]
    : found;
}

/**
 * Sharing it, as most apps do: who has access in general (only the people on its list, everyone in the
 * organization, or anyone with its public link, with what that page shows), who's on its list (added or taken off
 * right there), and its link to copy. A popover by its button.
 * `onDone` gets the item as it is now. With `send`, each change goes there instead (a folder's, asked about what's in
 * it), and `link` is its link.
 */
/** Sharing it, as it's shared now: public, private, or the organization's (Share's icon, in the page and the app's bar). */
export function SharedIcon({ item }: { item: Visible }) {
  if (item.visibility === "public") {
    return <PublicIcon />;
  }
  return isPrivate(item) ? <PrivateIcon /> : <OrgIcon />;
}

export function ShareButton(p: {
  space: SpaceOut;
  item: Visible & { title?: string | null };
  /** Makes each change in place of the item's PATCH (nobody invited from outside then). */
  send?: (body: Change) => Promise<void>;
  /** Its link, when it isn't an item's. */
  link?: string;
  /** The space's members and agents (who can be on its list). */
  members: readonly Member[] | undefined;
  onDone: (item: ItemDetail) => void;
  class?: string;
  /** Its panel's id, for opening it from elsewhere (the item's banner). */
  panel?: string;
  /** What its panel opens by when its button isn't shown (narrow screens: the item's menu opens it). */
  anchor?: { current: HTMLElement | null };
}) {
  const own = useId();
  const id = p.panel ?? own;
  const pop = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const people = p.item.private_to ?? [];
  const priv = isPrivate(p.item);
  const admin = atLeast(p.space.role, "admin");
  const pub = p.item.visibility === "public";
  // Sharing it outside the organization would share private attachments too: which, until you say so.
  const [pending, setPending] = useState<{ body: Change; attachments: readonly Named[] } | null>(null);
  const [goingPublic, setGoingPublic] = useState(false);
  const sheet = shell()?.platform === "ios";
  const [sheeted, setSheeted] = useState(false);
  const [patch, busy] = useAction(async (body: Change) => {
    if (p.send) {
      await p.send(body);
      return;
    }
    try {
      p.onDone(await api.patch<ItemDetail>(spaceApi(p.space, `/items/${p.item.id}`), { ...body, ver: p.item.ver }));
    } catch (e) {
      if (e instanceof ApiError && e.code === PRIVATE_ATTACHMENTS) {
        setPending({ body, attachments: (e.info["attachments"] as Named[] | undefined) ?? [] });
        return;
      }
      throw e;
    }
    if (body.visibility) {
      toast(t(MADE[body.visibility]));
    }
    const [invited] = body.invite ?? [];
    if (invited) {
      toast(t("visibility.invited", { email: invited }));
    }
  });
  // Only admins (who see everything anyway) take themselves off: anyone else would lose the item.
  const stays = (who: string) => who === me.value?.id && !admin;
  const others = (p.members ?? []).filter((m) => !people.includes(m.id));
  const guests = p.item.invited ?? [];
  const known = [...(p.members ?? []).flatMap((m) => m.email ?? []), ...guests.flatMap((g) => g.email ?? [])].map((e) =>
    e.toLowerCase(),
  );
  // Public: its public link; otherwise its short one (for whoever may open it).
  const link =
    pub && p.item.public ? `${globalThis.location.origin}/p/${p.item.public.token}` : (p.link ?? itemLink(p.item.id));
  const icon = <SharedIcon item={p.item} />;
  const panel = (
    <>
      {/* Asked over the panel, which stays open behind (a modal would close it). */}
      <ConfirmDialog
        nested={true}
        open={goingPublic}
        onClose={() => setGoingPublic(false)}
        title={t("visibility.publicTitle")}
        message={t("visibility.publicMessage")}
        confirmLabel={t("visibility.makePublic")}
        onConfirm={() => patch({ visibility: "public" })}
      />
      <ConfirmDialog
        nested={true}
        open={pending !== null}
        onClose={() => setPending(null)}
        title={t("visibility.privateAttachmentsTitle")}
        message={
          <>
            <p>{t("visibility.privateAttachments", { count: pending?.attachments.length ?? 0 })}</p>
            <ul class="mt-2 list-disc pl-5">
              {(pending?.attachments ?? []).map((a) => (
                <li key={a.id}>{titleOf(a)}</li>
              ))}
            </ul>
          </>
        }
        confirmLabel={t("visibility.shareThemToo")}
        onConfirm={() => (pending ? patch({ ...pending.body, share_private: true }) : undefined)}
      />
      {/* One change at a time: each is made on the version the last one left. */}
      <fieldset disabled={busy} class="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
        <section class="flex flex-col gap-2">
          <div class="flex items-center gap-3">
            <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600">
              {icon}
            </span>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <Select
                compact={true}
                aria-label={t("visibility.general")}
                value={p.item.visibility}
                options={[
                  { value: "private", label: t("visibility.private") },
                  { value: "org", label: t("visibility.orgChoice") },
                  { value: "public", label: t("visibility.public") },
                ]}
                onValue={(v) =>
                  v === p.item.visibility
                    ? undefined
                    : v === "public"
                      ? setGoingPublic(true) // anyone with the link: asked first
                      : patch({ visibility: v as Visibility })
                }
              />
              {pub ? null : (
                <span class="text-xs text-zinc-500">{t(priv ? "visibility.privateHint" : "visibility.orgHint")}</span>
              )}
            </div>
          </div>
        </section>
        <section class="flex flex-col gap-2">
          <h2 class={SECTION}>{t("visibility.peopleLabel")}</h2>
          {/* Many people: they scroll, the rest of the panel (general access, adding, the link) stays in view. */}
          <ul class="-mr-2 flex max-h-80 flex-col gap-1.5 overflow-y-auto overscroll-contain pr-2">
            {people.map((who) => (
              <li key={who} class="flex items-center gap-2">
                <Avatar id={who} name={nameOf(p.members, who)} size={24} />
                <span class="min-w-0 flex-1 truncate text-zinc-800">
                  {who === me.value?.id
                    ? t("visibility.you", { name: nameOf(p.members, who) })
                    : nameOf(p.members, who)}
                </span>
                {stays(who) ? (
                  <span class="text-xs text-zinc-400" title={t("visibility.youStay")}>
                    {t("visibility.stays")}
                  </span>
                ) : (
                  <IconButton
                    label={t("visibility.remove", { name: nameOf(p.members, who) })}
                    size="xs"
                    disabled={priv && people.length === 1}
                    onClick={() => patch({ private_to: people.filter((x) => x !== who) })}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </li>
            ))}
            {guests.map((g) => (
              <li key={g.id} class="flex items-center gap-2">
                <Avatar id={g.id} name={g.name} size={24} />
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="flex items-center gap-1.5">
                    <span class="truncate text-zinc-800">{g.name}</span>
                    <Badge tone="amber">{t("visibility.guest")}</Badge>
                  </span>
                  {g.email ? <span class="truncate text-xs text-zinc-500">{g.email}</span> : null}
                </span>
                <IconButton
                  label={t("visibility.remove", { name: g.name })}
                  size="xs"
                  onClick={() => patch({ guests: guests.filter((x) => x.id !== g.id).map((x) => x.id) })}
                >
                  <CloseIcon />
                </IconButton>
              </li>
            ))}
          </ul>
          {/* The organization's: everyone else in it has access too. */}
          {priv || others.length === 0 ? null : (
            <div class="flex items-center gap-2">
              <span class="flex -space-x-2">
                {others.slice(0, FACES).map((m) => (
                  <span key={m.id} class="rounded-full ring-2 ring-white">
                    <Avatar id={m.id} name={m.name} kind={m.kind} size={24} />
                  </span>
                ))}
              </span>
              <span class="text-zinc-600">
                {t(pub ? "visibility.orgMembersAndLink" : "visibility.orgMembers", { count: others.length })}
              </span>
            </div>
          )}
          <Combobox
            label={t("visibility.add")}
            placeholder={t("visibility.addPlaceholder")}
            search={(q) => Promise.resolve(peopleFor(q, others, known, !p.send))}
            onSelect={(o) =>
              o.value.startsWith(INVITE)
                ? patch({ invite: [o.value.slice(INVITE.length)] })
                : patch({ private_to: [...people, o.value] })
            }
          />
        </section>
        <div class="flex flex-col gap-2 border-t border-zinc-100 pt-3">
          {/* What the public page shows besides the item, and who may comment there. */}
          {pub ? (
            <div class="flex flex-col gap-1">
              <Switch
                label={<span class="text-xs">{t("visibility.showReview")}</span>}
                checked={p.item.public?.review ?? false}
                onCheck={(on) => patch({ public: { review: on } })}
              />
              <Switch
                label={<span class="text-xs">{t("visibility.showConversation")}</span>}
                checked={p.item.public?.conversation ?? false}
                onCheck={(on) => patch({ public: { conversation: on } })}
              />
              {p.item.public?.conversation ? (
                <Switch
                  label={<span class="text-xs">{t("visibility.openComments")}</span>}
                  checked={p.item.public.comments ?? false}
                  onCheck={(on) => patch({ public: { comments: on } })}
                />
              ) : null}
            </div>
          ) : null}
          <Input
            compact={true}
            readOnly={true}
            aria-label={t("visibility.link")}
            value={link}
            class="text-zinc-600"
            onFocus={(e) => e.currentTarget.select()}
            onValue={() => undefined}
          />
          <div class="flex justify-end">
            <CopyButton text={link} label={t("visibility.copyLink")} variant="secondary" icon={<LinkIcon />} />
          </div>
        </div>
      </fieldset>
    </>
  );
  const button = (
    <button
      ref={trigger}
      type="button"
      popovertarget={id}
      class={`${p.class ?? ""} ${ICON_ONLY_NARROW} ${busy ? "opacity-50" : ""}`}
    >
      {icon}
      <span>{t("visibility.share")}</span>
    </button>
  );
  // In the iOS app, a dialog: in the app's own sheet (its button's in the bar, not by the page: a popover would hang
  // from nothing). Opened as the popover would be (its button, the bar's, the banner's): instead of it.
  if (sheet) {
    return (
      <>
        {button}
        <div
          id={id}
          popover="manual"
          hidden={true}
          onBeforeToggle={(e) => {
            if (e.newState === "open") {
              e.preventDefault();
              setSheeted(true);
            }
          }}
        />
        <Dialog open={sheeted} onClose={() => setSheeted(false)} title={t("visibility.share")}>
          {panel}
        </Dialog>
      </>
    );
  }
  return (
    <>
      {button}
      <div
        ref={pop}
        id={id}
        popover="auto"
        role="dialog"
        aria-label={t("visibility.share")}
        class={PANEL}
        onToggle={(e) => {
          // By its button, or (hidden) by what it was opened from.
          const by = trigger.current?.offsetParent ? trigger.current : (p.anchor?.current ?? trigger.current);
          if (e.newState === "open" && pop.current && by) {
            placePopover(pop.current, by);
          }
        }}
      >
        {panel}
      </div>
    </>
  );
}
